import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  ZAPIER_EVENT_OPTIONS,
  createGoogleOAuthUrl,
  createZapierHook,
  disconnectGoogleWorkspace,
  deleteZapierHook,
  getGoogleWorkspaceStatus,
  listZapierHooks,
  syncGoogleWorkspace,
  testZapierHook,
  updateGooglePreferences,
  type ZapierEventType,
  type ZapierHook,
} from '../api/integrations';
import { useAuth } from '../context/AuthContext';

const PAYING_ROLES = new Set(['admin', 'owner', 'pro', 'enterprise']);

type ActionMessage = {
  type: 'success' | 'error';
  text: string;
};

type LatestSecret = {
  secret: string;
  eventType: ZapierEventType;
  targetUrl: string;
};

function formatTimestamp(value: number | null) {
  if (!value) return '—';
  return new Date(value * 1000).toLocaleString();
}

export function IntegrationsPage() {
  const { user } = useAuth();
  const isPaying = useMemo(() => (user ? PAYING_ROLES.has(user.role) : false), [user]);
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState<{ eventType: ZapierEventType | ''; targetUrl: string }>({
    eventType: '',
    targetUrl: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [latestSecret, setLatestSecret] = useState<LatestSecret | null>(null);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [activeTestId, setActiveTestId] = useState<string | null>(null);
  const [activeDeleteId, setActiveDeleteId] = useState<string | null>(null);
  const [googleMessage, setGoogleMessage] = useState<ActionMessage | null>(null);

  const {
    data: googleStatus,
    isLoading: isGoogleLoading,
    isError: isGoogleError,
    error: googleError,
  } = useQuery({
    queryKey: ['googleIntegration'],
    queryFn: getGoogleWorkspaceStatus,
    enabled: isPaying,
    staleTime: 30_000,
  });

  const connectGoogleMutation = useMutation({
    mutationFn: createGoogleOAuthUrl,
    onSuccess: ({ url }) => {
      window.open(url, 'crmify-google-oauth', 'width=520,height=680');
      setGoogleMessage({
        type: 'success',
        text: 'Complete the Google consent window, then use "Refresh status" to see the connection.',
      });
    },
    onError: (err: Error) => {
      setGoogleMessage({ type: 'error', text: err.message });
    },
  });

  const updateGooglePreferencesMutation = useMutation({
    mutationFn: updateGooglePreferences,
    onSuccess: (_, variables) => {
      const updatedContacts = typeof variables?.syncContacts !== 'undefined';
      const message = updatedContacts
        ? `Google contact sync ${variables?.syncContacts ? 'enabled' : 'disabled'}.`
        : 'Google contact preferences updated.';
      setGoogleMessage({ type: 'success', text: message });
      queryClient.invalidateQueries({ queryKey: ['googleIntegration'] });
    },
    onError: (err: Error) => {
      setGoogleMessage({ type: 'error', text: err.message });
    },
  });

  const syncGoogleMutation = useMutation({
    mutationFn: syncGoogleWorkspace,
    onSuccess: (response) => {
      const parts: string[] = [];
      if (response.created) parts.push(`${response.created} new`);
      if (response.updated) parts.push(`${response.updated} updated`);
      const summary = parts.length ? `Contacts synced: ${parts.join(', ')}.` : 'Contacts already up to date.';
      setGoogleMessage({ type: 'success', text: summary });
      queryClient.invalidateQueries({ queryKey: ['googleIntegration'] });
    },
    onError: (err: Error) => {
      setGoogleMessage({ type: 'error', text: err.message });
    },
  });

  const disconnectGoogleMutation = useMutation({
    mutationFn: disconnectGoogleWorkspace,
    onSuccess: () => {
      setGoogleMessage({ type: 'success', text: 'Google Workspace disconnected.' });
      queryClient.invalidateQueries({ queryKey: ['googleIntegration'] });
    },
    onError: (err: Error) => {
      setGoogleMessage({ type: 'error', text: err.message });
    },
  });

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['zapierHooks'],
    queryFn: listZapierHooks,
    enabled: isPaying,
  });

  const availableEvents = data?.availableEvents ?? (isPaying ? ZAPIER_EVENT_OPTIONS : []);

  useEffect(() => {
    if (!formState.eventType && availableEvents.length) {
      setFormState((state) => ({ ...state, eventType: availableEvents[0] }));
    }
  }, [availableEvents, formState.eventType]);

  const createHookMutation = useMutation({
    mutationFn: createZapierHook,
    onSuccess: (response) => {
      setLatestSecret({
        secret: response.hook.secret,
        eventType: response.hook.eventType,
        targetUrl: response.hook.targetUrl,
      });
      setFormState({ eventType: availableEvents[0] ?? '', targetUrl: '' });
      setActionMessage({ type: 'success', text: 'Zapier hook created. Copy the secret below.' });
      setFormError(null);
      queryClient.invalidateQueries({ queryKey: ['zapierHooks'] });
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  const deleteHookMutation = useMutation({
    mutationFn: deleteZapierHook,
    onSuccess: () => {
      setActionMessage({ type: 'success', text: 'Zapier hook removed.' });
      queryClient.invalidateQueries({ queryKey: ['zapierHooks'] });
    },
    onError: (err: Error) => {
      setActionMessage({ type: 'error', text: err.message });
    },
    onSettled: () => setActiveDeleteId(null),
  });

  const testHookMutation = useMutation({
    mutationFn: testZapierHook,
    onSuccess: () => {
      setActionMessage({ type: 'success', text: 'Test event sent successfully.' });
    },
    onError: (err: Error) => {
      setActionMessage({ type: 'error', text: err.message });
    },
    onSettled: () => setActiveTestId(null),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setActionMessage(null);

    if (!formState.eventType) {
      setFormError('Select an event to watch.');
      return;
    }

    const trimmedUrl = formState.targetUrl.trim();
    if (!trimmedUrl) {
      setFormError('Enter the Zapier webhook URL.');
      return;
    }

    createHookMutation.mutate({ eventType: formState.eventType, targetUrl: trimmedUrl });
  }

  function handleDelete(id: string) {
    if (deleteHookMutation.isPending) return;
    setActionMessage(null);
    setActiveDeleteId(id);
    deleteHookMutation.mutate(id);
  }

  function handleTest(id: string) {
    if (testHookMutation.isPending) return;
    setActionMessage(null);
    setActiveTestId(id);
    testHookMutation.mutate(id);
  }

  function handleGoogleConnect() {
    if (connectGoogleMutation.isPending) return;
    setGoogleMessage(null);
    connectGoogleMutation.mutate();
  }

  function handleGoogleSync() {
    if (syncGoogleMutation.isPending) return;
    setGoogleMessage(null);
    syncGoogleMutation.mutate();
  }

  function handleGoogleDisconnect() {
    if (disconnectGoogleMutation.isPending) return;
    setGoogleMessage(null);
    disconnectGoogleMutation.mutate();
  }

  function handleGoogleRefresh() {
    setGoogleMessage(null);
    queryClient.invalidateQueries({ queryKey: ['googleIntegration'] });
  }

  function handleGooglePreferenceToggle(enabled: boolean) {
    if (updateGooglePreferencesMutation.isPending) return;
    if (!googleStatusData?.connected) return;
    setGoogleMessage(null);
    updateGooglePreferencesMutation.mutate({ syncContacts: enabled });
  }

  const hooks = (data?.hooks ?? []) as ZapierHook[];
  const queryError = isError && error instanceof Error ? error.message : null;
  const googleStatusData = googleStatus ?? null;
  const googleStatusError = isGoogleError && googleError instanceof Error ? googleError.message : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Integrations</h1>
          <p>Connect Google Workspace and Zapier to keep your CRM in sync.</p>
        </div>
      </div>

      {!isPaying ? (
        <div className="card">
          <h2>Unlock Google Workspace</h2>
          <p>Google Workspace automation is available on paid plans. Upgrade to sync Google contacts and schedule meetings.</p>
        </div>
      ) : (
        <div className="card">
          <h2>Google Workspace</h2>
          <p>Keep Google contacts in sync and schedule Google Calendar meetings from your tasks.</p>
          {isGoogleLoading ? (
            <p>Loading Google connection status…</p>
          ) : googleStatusError ? (
            <p className="form-error">{googleStatusError}</p>
          ) : (
            <div>
              {googleMessage && <div className={`notice notice--${googleMessage.type}`}>{googleMessage.text}</div>}

              {googleStatusData?.connected ? (
                <div>
                  <div>
                    <div><strong>Connected account:</strong> {googleStatusData.email}</div>
                    <div className="muted">Last contact sync: {formatTimestamp(googleStatusData.lastContactsSyncAt ?? null)}</div>
                    <div className="muted">Automatic contact sync runs every 30 minutes when enabled.</div>
                    {googleStatusData.requiresReauthForContacts && (
                      <div className="notice notice--warning">
                        Reconnect Google Workspace to grant contacts access before syncing.
                      </div>
                    )}
                    {googleStatusData.requiresReauthForMeetings && (
                      <div className="notice notice--warning">
                        Reconnect Google Workspace to grant calendar access before scheduling meetings from tasks.
                      </div>
                    )}
                  </div>
                  <div className="form-grid" style={{ marginTop: '1rem' }}>
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(googleStatusData.syncContacts)}
                        onChange={(event) => handleGooglePreferenceToggle(event.target.checked)}
                        disabled={updateGooglePreferencesMutation.isPending || Boolean(googleStatusData.requiresReauthForContacts)}
                      />
                      <span>Automatically sync Google contacts into CRMify.io</span>
                    </label>
                  </div>
                  {googleStatusData.canScheduleMeetings && !googleStatusData.requiresReauthForMeetings && (
                    <div className="muted" style={{ marginTop: '0.75rem' }}>
                      Meeting scheduling from tasks is enabled for this account.
                    </div>
                  )}
                  <div className="page-header-actions">
                    <button
                      className="btn btn--secondary"
                      type="button"
                      onClick={handleGoogleSync}
                      disabled={syncGoogleMutation.isPending || Boolean(googleStatusData.requiresReauthForContacts)}
                    >
                      {syncGoogleMutation.isPending ? 'Syncing contacts…' : 'Sync contacts'}
                    </button>
                    <button
                      className="btn btn--secondary"
                      type="button"
                      onClick={handleGoogleRefresh}
                    >
                      Refresh status
                    </button>
                    <button
                      className="btn"
                      type="button"
                      onClick={handleGoogleDisconnect}
                      disabled={disconnectGoogleMutation.isPending}
                    >
                      {disconnectGoogleMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="muted">Connect your Google account to sync contacts and schedule meetings from CRMify tasks.</p>
                  <div className="page-header-actions">
                    <button
                      className="btn btn--primary"
                      type="button"
                      onClick={handleGoogleConnect}
                      disabled={connectGoogleMutation.isPending}
                    >
                      {connectGoogleMutation.isPending ? 'Opening Google…' : 'Connect Google Workspace'}
                    </button>
                    <button
                      className="btn btn--secondary"
                      type="button"
                      onClick={handleGoogleRefresh}
                      disabled={isGoogleLoading}
                    >
                      Refresh status
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isPaying ? (
        <div className="card">
          <h2>Unlock Zapier automation</h2>
          <p>Zapier integrations are available on paid plans. Upgrade to trigger workflows across your tools.</p>
        </div>
      ) : (
        <>
          {actionMessage && (
            <div className={`notice notice--${actionMessage.type}`}>
              {actionMessage.text}
            </div>
          )}

          {latestSecret && (
            <div className="notice notice--success">
              <strong>Copy your signing secret:</strong>
              <div><code>{latestSecret.secret}</code></div>
              <p>Store this secret securely. It is shown only once for {latestSecret.eventType} targeting {latestSecret.targetUrl}.</p>
            </div>
          )}

          <div className="card">
            <h2>Create Zapier hook</h2>
            <p>Add a Zapier Catch Hook URL to receive events when CRMify.io data changes.</p>
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-grid">
                <label>
                  Event
                  <select
                    value={formState.eventType}
                    onChange={(event) => setFormState((state) => ({ ...state, eventType: event.target.value as ZapierEventType }))}
                    disabled={!availableEvents.length || createHookMutation.isPending}
                    required
                  >
                    <option value="" disabled>
                      Select event…
                    </option>
                    {availableEvents.map((eventType) => (
                      <option key={eventType} value={eventType}>
                        {eventType}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Zapier webhook URL
                  <input
                    type="url"
                    value={formState.targetUrl}
                    onChange={(event) => setFormState((state) => ({ ...state, targetUrl: event.target.value }))}
                    placeholder="https://hooks.zapier.com/..."
                    required
                  />
                </label>
              </div>
              {formError && <p className="form-error">{formError}</p>}
              <div className="form-actions">
                <button className="btn btn--primary" type="submit" disabled={createHookMutation.isPending}>
                  {createHookMutation.isPending ? 'Creating…' : 'Create hook'}
                </button>
              </div>
            </form>
          </div>

          <div className="card">
            <h2>Active hooks</h2>
            {isLoading ? (
              <p>Loading Zapier hooks…</p>
            ) : queryError ? (
              <p className="form-error">{queryError}</p>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Target URL</th>
                      <th>Status</th>
                      <th>Last success</th>
                      <th>Last error</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hooks.map((hook) => (
                      <tr key={hook.id}>
                        <td>{hook.eventType}</td>
                        <td>
                          <div>{hook.targetUrl}</div>
                          {hook.secretPreview && <div className="muted">Secret {hook.secretPreview}</div>}
                        </td>
                        <td>{hook.status}</td>
                        <td>{formatTimestamp(hook.lastSuccessAt)}</td>
                        <td>
                          {hook.lastErrorAt ? (
                            <div>
                              {formatTimestamp(hook.lastErrorAt)}
                              {hook.errorMessage ? <div className="muted">{hook.errorMessage}</div> : null}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <div className="page-header-actions">
                            <button
                              className="btn btn--secondary"
                              type="button"
                              onClick={() => handleTest(hook.id)}
                              disabled={testHookMutation.isPending}
                            >
                              {testHookMutation.isPending && activeTestId === hook.id ? 'Testing…' : 'Send test'}
                            </button>
                            <button
                              className="btn"
                              type="button"
                              onClick={() => handleDelete(hook.id)}
                              disabled={deleteHookMutation.isPending}
                            >
                              {deleteHookMutation.isPending && activeDeleteId === hook.id ? 'Removing…' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!hooks.length && (
                      <tr>
                        <td colSpan={6}>No Zapier hooks yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
